import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { isBrowserOffline } from "../utils/offline";
import {
  fetchAuthMe,
  getUserRemoteId,
  getUserDisplayName,
  isAuthMeAdmin,
} from "../api/authMe";

const CurrentUserContext = createContext(null);

// The id of the last signed-in user, so rows saved on the device resolve (person, avatar) in their
// first frame instead of after /auth/me. Once /auth/me has answered online, its answer alone counts.
const SAVED_REMOTE_ID_KEY = "hami_current_user_remote_id";

function readSavedRemoteId() {
  try {
    return localStorage.getItem(SAVED_REMOTE_ID_KEY) || null;
  } catch {
    return null;
  }
}

export function useCurrentUser() {
  return useContext(CurrentUserContext);
}

export function CurrentUserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savedRemoteId] = useState(readSavedRemoteId);

  useEffect(() => {
    let cancelled = false;
    fetchAuthMe()
      .then((u) => {
        if (!cancelled) setUser(u);
        const remoteId = getUserRemoteId(u);
        if (remoteId == null) return;
        try {
          localStorage.setItem(SAVED_REMOTE_ID_KEY, String(remoteId));
        } catch {
          // storage full: the next start resolves after /auth/me, as before
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({
      user,
      remoteId: getUserRemoteId(user) ?? (loading || isBrowserOffline() ? savedRemoteId : null),
      displayName: getUserDisplayName(user),
      isAdmin: isAuthMeAdmin(user),
      loading,
    }),
    [user, loading, savedRemoteId]
  );

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
}
